import { useLocation } from "react-router-dom";
import { Breadcrumbs, Link } from "@mui/material";
import { NavigateNext } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";

/**
 * 경로 문자열 정규화 (앞 슬래시 통일)
 */
function normalizePath(p) {
  if (!p || typeof p !== "string") return "";
  return p.startsWith("/") ? p : `/${p}`;
}

/**
 * 자식 중 동적 세그먼트(:id, :gcId 등) 찾기
 */
function findDynamicChild(item) {
  return item.child?.find((c) => c.key && c.key.startsWith(":"));
}

export default function Breadcrumb({ navData }) {
  const location = useLocation();
  const theme = useTheme();
  const pathSegments = location.pathname.split("/").filter(Boolean);
  const nP = pathSegments.map((p) => (Number.isNaN(Number(p)) ? p : ":id"));

  function findBreadcrumbPath(navItems, segments) {
    const fullPathNorm = normalizePath(segments.join("/"));

    function findByPath(items, parentPath = [], parentChain = []) {
      for (const item of items) {
        const itemPathNorm = normalizePath(item.path || "");
        const matches =
          itemPathNorm && (fullPathNorm === itemPathNorm || fullPathNorm.startsWith(itemPathNorm + "/"));

        if (matches) {
          const result = [...parentChain, item];

          if (item.child && item.child.length > 0) {
            const childResult = findByPath(item.child, itemPathNorm, result);
            if (childResult) return childResult;

            const pathPartCount = fullPathNorm.split("/").filter(Boolean).length;
            const itemPartCount = itemPathNorm.split("/").filter(Boolean).length;
            if (pathPartCount > itemPartCount) {
              const dynamicChild = findDynamicChild(item);
              if (dynamicChild) return [...result, dynamicChild];
            }
          }

          return result;
        }

        if (item.child && item.child.length > 0) {
          const childResult = findByPath(item.child, null, [...parentChain, item]);
          if (childResult) return childResult;
        }
      }
      return null;
    }

    const pathResult = findByPath(navItems);
    if (pathResult && pathResult.length > 0) return pathResult;

    // path 매칭 실패 시 key 기반 매칭 (연단체·월단체 등)
    let path = [];
    let currentLevel = navItems;
    for (const segment of segments) {
      const match = currentLevel?.find?.((item) => item.key === segment);
      if (!match) break;
      path.push(match);
      currentLevel = match.child;
    }
    return path;
  }

  const breadcrumbPath = findBreadcrumbPath(navData || [], nP);
  const filteredPath = (breadcrumbPath || []).filter((item) => item.key !== "modify");

  if (filteredPath.length === 0) {
    return null;
  }

  return (
    <Breadcrumbs
      aria-label="breadcrumb"
      sx={{
        color: theme.palette.common.darkGrey,
        ".MuiBreadcrumbs-separator": {
          margin: "0 4px !important",
        },
      }}
      separator={
        <NavigateNext
          fontSize="20px"
          sx={{ verticalAlign: "middle", color: "#79818A" }}
        />
      }
    >
      {filteredPath.map((item, index) => {
        const isLast = index === filteredPath.length - 1;
        const originalIndex = breadcrumbPath.indexOf(item);
        const nextItem = breadcrumbPath[originalIndex + 1];

        // 부모 클릭 시 첫 번째 자식 페이지로 이동, 마지막 항목은 현재 경로
        let to;
        if (!isLast && nextItem) {
          to = nextItem.path ? normalizePath(nextItem.path) : "/" + pathSegments.slice(0, originalIndex + 2).join("/");
        } else {
          to = "/" + pathSegments.slice(0, originalIndex + 1).join("/");
        }

        const displayName = item.breadcrumbName || (typeof item.name === "string" ? item.name : item.key || "");

        return (
          <Link
            sx={{
              fontSize: "16px !important",
              fontWeight: isLast ? "600" : "500",
              color: isLast ? "black !important" : "#79818A",
            }}
            key={`${item.key}-${index}`}
            to={to}
            underline="none"
            color={isLast ? "black" : "#79818A"}
          >
            {displayName}
          </Link>
        );
      })}
    </Breadcrumbs>
  );
}
